(() => {
  const STORAGE_KEY = 'vlou-recently-viewed';
  const MAX_STORED_PRODUCTS = 12;

  function getStoredHandles() {
    try {
      const stored = JSON.parse(localStorage.getItem(STORAGE_KEY));

      return Array.isArray(stored)
        ? stored.filter(
            (handle) =>
              typeof handle === 'string' &&
              handle.trim() !== ''
          )
        : [];
    } catch (error) {
      console.warn('Recently viewed products could not be read.', error);
      return [];
    }
  }

  function saveHandles(handles) {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(handles.slice(0, MAX_STORED_PRODUCTS))
      );
    } catch (error) {
      console.warn('Recently viewed products could not be saved.', error);
    }
  }

  function escapeHtml(value) {
    return String(value ?? '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  function getActiveCurrency() {
    return (
      window.Shopify?.currency?.active ||
      window.Shopify?.currency?.default ||
      'USD'
    );
  }

  function formatMoneyParts(cents) {
  const amount = Number(cents) / 100;
  const currency = getActiveCurrency();

  const currencySymbols = {
    USD: '$',
    CAD: '$',
    AUD: '$',
    NZD: '$',
    EUR: '€',
    GBP: '£',
    JPY: '¥',
    CNY: '¥',
    KRW: '₩',
    CHF: 'CHF ',
    SEK: 'kr ',
    NOK: 'kr ',
    DKK: 'kr '
  };

  const symbol = currencySymbols[currency] || '';

  const formattedNumber = new Intl.NumberFormat(
    document.documentElement.lang || 'en-US',
    {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }
  ).format(amount);

  return {
    amount: `${symbol}${formattedNumber}`,
    currency
  };
}

  function getProductPrice(product) {
    const availableVariants = product.variants.filter(
      (variant) => variant.available
    );

    const variantsToUse =
      availableVariants.length > 0
        ? availableVariants
        : product.variants;

    const prices = variantsToUse
      .map((variant) => Number(variant.price))
      .filter(Number.isFinite);

    return prices.length ? Math.min(...prices) : 0;
  }

  function getCompareAtPrice(product) {
    const comparePrices = product.variants
      .map((variant) => Number(variant.compare_at_price))
      .filter(
        (price) =>
          Number.isFinite(price) &&
          price > 0
      );

    return comparePrices.length
      ? Math.max(...comparePrices)
      : 0;
  }

  function getProductImage(product) {
    if (product.featured_image) {
      return product.featured_image;
    }

    if (
      Array.isArray(product.images) &&
      product.images.length > 0
    ) {
      return product.images[0];
    }

    return '';
  }

  function buildPriceMarkup(price, compareAtPrice, sale) {
    const formattedPrice = formatMoneyParts(price);
    const formattedCompareAtPrice =
      formatMoneyParts(compareAtPrice);

    if (sale) {
      return `
        <div class="price price--on-sale">
          <div class="price__container">
            <div class="price__sale">
              <span class="price-item price-item--sale price-item--last vlou-price">
                <span class="vlou-price-number">
                  ${escapeHtml(formattedPrice.amount)}
                </span>

                <span class="vlou-price-currency">
                  ${escapeHtml(formattedPrice.currency)}
                </span>
              </span>

              <s class="price-item price-item--regular vlou-price vlou-price--compare">
                <span class="vlou-price-number">
                  ${escapeHtml(formattedCompareAtPrice.amount)}
                </span>

                <span class="vlou-price-currency">
                  ${escapeHtml(formattedCompareAtPrice.currency)}
                </span>
              </s>
            </div>
          </div>
        </div>
      `;
    }

    return `
      <div class="price">
        <div class="price__container">
          <div class="price__regular">
            <span class="price-item price-item--regular vlou-price">
              <span class="vlou-price-number">
                ${escapeHtml(formattedPrice.amount)}
              </span>

              <span class="vlou-price-currency">
                ${escapeHtml(formattedPrice.currency)}
              </span>
            </span>
          </div>
        </div>
      </div>
    `;
  }

  function buildProductCard(product, sectionId) {
    const handle = escapeHtml(product.handle);
    const title = escapeHtml(product.title);
    const productId = escapeHtml(product.id);
    const url = `/products/${handle}`;
    const image = getProductImage(product);
    const price = getProductPrice(product);
    const compareAtPrice = getCompareAtPrice(product);
    const available = product.available !== false;

    const sale =
      available &&
      compareAtPrice > 0 &&
      compareAtPrice > price;

    const imageMarkup = image
      ? `
        <div class="card__media">
          <div class="media media--transparent media--hover-effect">
            <img
              src="${escapeHtml(image)}&width=720"
              srcset="
                ${escapeHtml(image)}&width=360 360w,
                ${escapeHtml(image)}&width=533 533w,
                ${escapeHtml(image)}&width=720 720w
              "
              sizes="(min-width: 990px) calc((100vw - 130px) / 4), calc((100vw - 35px) / 2)"
              alt="${title}"
              loading="lazy"
              width="720"
              height="900"
              class="motion-reduce"
            >
          </div>
        </div>
      `
      : '';

    let badgeMarkup = '';

    if (!available) {
      badgeMarkup = `
        <span class="badge badge--bottom-left color-inverse">
          Sold out
        </span>
      `;
    } else if (sale) {
      badgeMarkup = `
        <span class="badge badge--bottom-left color-accent-2">
          Sale
        </span>
      `;
    }

    const priceMarkup = buildPriceMarkup(
      price,
      compareAtPrice,
      sale
    );

    return `
      <li class="grid__item">
        <div class="card-wrapper product-card-wrapper underline-links-hover">
          <div
            class="card card--${
              escapeHtml(
                window.VlouRecentlyViewedCardStyle || 'standard'
              )
            } card--media"
            style="--ratio-percent: 125%;"
          >
            <div
              class="card__inner color-background-1 gradient ratio"
              style="--ratio-percent: 125%;"
            >
              ${imageMarkup}

              <div class="card__content">
                <div class="card__information">
                  <h3
                    class="card__heading"
                    id="RecentlyViewedTitle-${sectionId}-${productId}"
                  >
                    <a
                      href="${url}"
                      class="full-unstyled-link"
                      aria-labelledby="RecentlyViewedTitle-${sectionId}-${productId}"
                    >
                      ${title}
                    </a>
                  </h3>
                </div>

                <div class="card__badge bottom left">
                  ${badgeMarkup}
                </div>
              </div>
            </div>

            <div class="card__content">
              <div class="card__information">
                <h3
                  class="card__heading h5"
                  id="RecentlyViewedCardTitle-${sectionId}-${productId}"
                >
                  <a
                    href="${url}"
                    class="full-unstyled-link"
                    aria-labelledby="RecentlyViewedCardTitle-${sectionId}-${productId}"
                  >
                    ${title}
                  </a>
                </h3>

                <div class="card-information">
                  ${priceMarkup}
                </div>
              </div>

              <div class="card__badge bottom left">
                ${badgeMarkup}
              </div>
            </div>
          </div>
        </div>
      </li>
    `;
  }

  async function fetchProduct(handle) {
    const root = window.Shopify?.routes?.root || '/';

    const response = await fetch(
      `${root}products/${encodeURIComponent(handle)}.js`,
      {
        headers: {
          Accept: 'application/json'
        }
      }
    );

    if (!response.ok) {
      throw new Error(`Could not load product: ${handle}`);
    }

    return response.json();
  }

  async function initializeSection(section) {
    const currentHandle =
      section.dataset.currentProductHandle || '';

    const limit =
      Number(section.dataset.limit) || 4;

    const minimum =
      Number(section.dataset.minimum) || 2;

    const grid = section.querySelector(
      '[data-recently-viewed-grid]'
    );

    if (!grid) return;

    const storedHandles = getStoredHandles();

    const handlesToDisplay = storedHandles
      .filter((handle) => handle !== currentHandle)
      .slice(0, limit);

    if (handlesToDisplay.length < minimum) {
      section.hidden = true;
      return;
    }

    const results = await Promise.allSettled(
      handlesToDisplay.map(fetchProduct)
    );

    const products = results
      .filter((result) => result.status === 'fulfilled')
      .map((result) => result.value)
      .filter(
        (product) =>
          product &&
          product.handle !== currentHandle
      );

    if (products.length < minimum) {
      section.hidden = true;
      return;
    }

    grid.innerHTML = products
      .slice(0, limit)
      .map((product) =>
        buildProductCard(product, section.id)
      )
      .join('');

    section.hidden = false;
  }

  async function initializeRecentlyViewed() {
    const sections = document.querySelectorAll(
      '[data-recently-viewed]'
    );

    if (!sections.length) return;

    await Promise.all(
      Array.from(sections).map((section) =>
        initializeSection(section).catch((error) => {
          console.warn(
            'Recently viewed section failed to load.',
            error
          );

          section.hidden = true;
        })
      )
    );
  }

  document.addEventListener(
    'DOMContentLoaded',
    initializeRecentlyViewed
  );

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      const section = event.target.matches(
        '[data-recently-viewed]'
      )
        ? event.target
        : event.target.querySelector(
            '[data-recently-viewed]'
          );

      if (!section) return;

      initializeSection(section).catch((error) => {
        console.warn(
          'Recently viewed section failed to reload.',
          error
        );

        section.hidden = true;
      });
    }
  );

  window.VlouRecentlyViewed = {
    add(handle) {
      if (
        !handle ||
        typeof handle !== 'string'
      ) {
        return;
      }

      const storedHandles = getStoredHandles().filter(
        (storedHandle) => storedHandle !== handle
      );

      storedHandles.unshift(handle);
      saveHandles(storedHandles);
    },

    clear() {
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch (error) {
        console.warn(
          'Recently viewed products could not be cleared.',
          error
        );
      }
    }
  };
})();